import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => (id ? getComicById(id) : undefined), [id]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 韩漫在线阅读_韩漫漫画网`;
    } else {
      document.title = '漫画未找到 - 韩漫漫画网';
    }
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-3">漫画不存在</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的漫画可能已下架或链接有误</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${comic.title} - 韩漫在线观看`,
    'description': comic.description,
    'author': {
      '@type': 'Person',
      'name': comic.author,
    },
    'datePublished': comic.updatedAt,
    'dateModified': comic.updatedAt,
    'image': comic.cover,
    'url': `https://www.hanmancomic.com/article/${comic.id}`,
    'publisher': {
      '@type': 'Organization',
      'name': '韩漫漫画网',
    },
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: `${comic.category}漫画`, to: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row gap-8 md:gap-10 mb-14">
              <div className="flex-shrink-0 w-full lg:w-[300px]">
                <div className="rounded-lg overflow-hidden aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 最新高清免费韩国漫画`}
                    title={`${comic.title} - 韩漫在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="text-sm text-foreground-600">{comic.author} · 著</span>
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-accent-600">
                    <i className="ri-star-fill text-sm"></i>
                    {comic.rating}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {(comic.views / 10000).toFixed(1)}万次阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
                  <h2 className="text-sm font-bold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line mr-1.5"></i>开始阅读第1话
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-1.5"></i>加入收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共 {comic.chapters.length} 话
                </span>
              </h2>
              <div className="bg-background-100 rounded-lg border border-background-200/70 divide-y divide-background-200/70">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between px-4 py-3.5 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs text-foreground-400 font-medium whitespace-nowrap">
                        {chapter.number}
                      </span>
                      <span className="text-sm text-foreground-800 truncate">{chapter.title}</span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-4">{chapter.updatedAt}</span>
                  </div>
                ))}
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="pb-14">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}